import React from 'react'
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import GenericRowActionsMenu from "@ui-components/Menu/GenericRowActionsMenu";

export interface CloudElement {
  id: number;
  elementType: string;
  elementId: string;
  vpcId: string;
  region: string;
  tags: string;
  discoveredAt: string;
  reflectorInstanceId: number;
  createdAt: string;
  updatedAt: string;
}

export interface Interface {
  id: number;
  interface: string;
  ipAddress: string;
  macAddress: string;
  type: string;
  vpcId: string;
  cloudSubnetId: string;
}

export interface Reflector {
  id: string;
  name: string;
  description: string;
  subId: string;
  status: string;
  /**
   * Estado de licencia del reflector, tal como lo devuelve el listado
   * (CTX-355). `licenseReason` usa el vocabulario de LICENSE_VALIDITY_REASONS
   * del backend -- valid, expired, suspended, not_started, no_license -- y no
   * uno propio, para que el badge y el bloqueo de la API no se contradigan.
   */
  licenseSerial?: string | null;
  licenseValid?: boolean;
  licenseReason?: string;
  payg?: boolean;
  /** Hay licencia BYOL asignada, o sea algo que liberar. */
  hasLicense?: boolean;
  availability?: number | null;
  cloud: string;
  region: string;
  cluster: string;
  clusterId?: string;
  version: string;
  lastSync: string;
  createdAt: string;
  vpcId: string;
  /**
   * Legacy comma-separated tag labels parsed into an array. Source of
   * truth for the production legacy modal (`EditableTagList` legacy
   * mode). Post-KD-563-2 this is populated from the response's
   * `tagsLegacy` field (the rich pivot tags live in `tagsRich`).
   */
  tags?: string[];
  /**
   * Rich tags (`{id, name, color, category}`) from the Tags Manager
   * pivot, exposed inline on detail GETs per TAGS-API-CONTRACT.md §5.5.
   * Preferred source for seeding the picker (`initialAssignedTags`) —
   * skips the §5.1 round-trip on mount.
   */
  tagsRich?: ResourceTagSummary[];
  data?: {
    ha?: string;
    cni?: Record<string, string>;
    cloud?: string;
    ha_ip?: string | null;
    region?: string;
    cloudSubnetId?: string;
  };
  cloudElements?: CloudElement[];
  interfaces?: Interface[];
  // ...other fields as needed
  license?: {
    id: number;
    serial: string;
    type: string;
    status: number;
    total: number;
    activated: number;
    available: number;
    startDate: string;
    endDate: string;
  };
}

const ReflectorIdCell = ({ row }: { row: Reflector }) => {
  const navigate = useNavigate();
  return (
    <button
      className="
        hover:text-semantic-text-brand 
        hover:underline
        focus:underline 
        active:underline
        font-medium
        underline
        text-semantic-text-brand
      "
      onClick={() => navigate(`/reflector-v2/management/bgp-reflectors/details/${row.id}`, { 
        state: { selectedReflectorRow: row } 
      })}
    >
      {row.name}
    </button>
  );
};

export const reflectorColumns: GenericTableColumn<Reflector>[] = [
  {
    header: "Reflector ID",
    accessor: "name",
    renderCell: (row) => <ReflectorIdCell row={row} />,
  },
  {
    header: "Name",
    accessor: "description",
    renderCell: (row) => row.description,
  },
  {
    header: "UUID",
    accessor: "subId",
    renderCell: (row) => row.subId || "-",
  },
  {
    header: "Status",
    accessor: "status",
    renderCell: (row) => {
      let variant: "success" | "warning" | "error" | "default" = "default";
      
      if (row.status === "Online") {
        variant = "success";
      } else if (row.status === "Offline") {
        variant = "error";
      }
      
      return (
        <GenericTag
          label={row.status}
          variant={variant}
          size="small"
          rounded="full"
        />
      );
    },
  },
  {
    header: "Cloud",
    accessor: "cloud",
    renderCell: (row) => (
      <GenericTag
        label={row.cloud}
        variant={row.cloud === "AWS" ? "warning" : row.cloud === "GCP" ? "success" : "neutral"}
        size="small"
        rounded="full"
      />
    ),
  },
  {
    header: "Region",
    accessor: "region",
    renderCell: (row) => (
      <GenericTag
        label={row.region}
        variant="neutral"
        size="small"
        rounded="full"
      />
    ),
  },
  {
    header: "Cluster",
    accessor: "cluster",
    renderCell: (row) => (
      <GenericTag
        label={row.cluster}
        variant="neutral"
        size="small"
        rounded="full"
      />
    ),
  },
  {
    header: "Version",
    accessor: "version",
  },
  {
    header: "Last Sync",
    accessor: "lastSync",
    renderCell: (row) => formatDateDMY(row.lastSync),
  },
  
];

/**
 * Un reflector ofrece la liberacion sólo cuando hay una licencia BYOL que
 * soltar. PAYG es otro modelo de licenciamiento y uno ya liberado no tiene
 * nada que devolver, asi que en esos casos no se muestra una accion que el
 * backend rechazaria.
 *
 * El caso del cluster no se filtra aca a proposito: eso lo decide el backend,
 * y la accion se ofrece igual para que el rechazo explique que hay que
 * deshacer el cluster primero (CTX-351).
 */
export const canReleaseLicense = (row: Reflector): boolean =>
  Boolean(row.hasLicense) && !row.payg;

export interface ReflectorRowHandlers {
  onReleaseLicense: (row: Reflector) => void;
}

/**
 * Las columnas del listado mas la de acciones.
 *
 * Es una funcion y no una constante porque la accion necesita el handler de la
 * pagina. `reflectorColumns` se mantiene para quien sólo lee las columnas de
 * datos.
 */
export const buildReflectorColumns = (
  handlers: ReflectorRowHandlers
): GenericTableColumn<Reflector>[] => [
  ...reflectorColumns,
  {
    id: "actions",
    header: "",
    // Sin `width` a proposito: GenericTable pasa la tabla entera a `table-fixed`
    // en cuanto UNA columna declara ancho, y las nueve columnas de datos se
    // reparten el espacio en partes iguales sin mirar su contenido — el UUID
    // termina debajo del pill de Status. El boton del menu ya trae su propio
    // ancho, asi que no hace falta (CTX-357).
    renderCell: (row) => (
      <div className="flex justify-end">
        <GenericRowActionsMenu
          ariaLabel={`Actions for ${row.name}`}
          actions={
            canReleaseLicense(row)
              ? [
                  {
                    id: "release-license",
                    label: "Release license",
                    onSelect: () => handlers.onReleaseLicense(row),
                  },
                ]
              : []
          }
        />
      </div>
    ),
  },
];

export const TABS = ["Device View", "Cluster View"];

function formatDateDMY(dateString: string) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  return format(date, "dd/MM/yyyy");
}



